import { Analytics } from '@util/Analytics';
import { recordError } from '@util/Errors';
import {
  MedicareAccessAttemptLog,
  MedicareAccessEligibilityResultCode,
  MedicareAccessLifecycleStatus,
  MedicareAccessTrack,
} from 'hydra-data-types';
import React, { ReactElement, useEffect } from 'react';
import { LayoutScreenConfig, ScreenType } from '../../../config/FlowTypes';
import {
  medicareAccessAttemptLogsCollectionRef,
  medicareAccessUserTrackDocRef,
} from '../../../helpers/CommonDatabaseOperations';
import { store, useAppStore } from '../../../store/Store';
import { checkMedicareAccessEligibility } from '../../../store/userData/effects/CheckMedicareAccessEligibility';
import { getEligibilityOutcome, isDirectMedicareFlow } from '../../../util/MedicareAccess';
import { getMedicareAccessStage } from '../../../util/MedicareAccessSymptomUtils';
import { FlowContentBody, ScreenWrapper } from '../../styles/Containers';
import { NavigationProps } from '../FlowNavigation';
import { AiLoadingLayout, AiLoadingLayoutConfig } from '../layouts/AiLoadingLayout';

const ELIGIBILITY_RESULT_TIMEOUT_MS = 10000;

export type MedicareAccessCoverageLoadingLayoutConfig = Omit<AiLoadingLayoutConfig, 'onComplete'>;

export type MedicareAccessCoverageLoadingScreenConfig<ScreenNames extends string> =
  LayoutScreenConfig<
    ScreenType.MedicareAccessCoverageLoading,
    MedicareAccessCoverageLoadingLayoutConfig,
    ScreenNames
  > & {
    /** Where a `rejected` eligibility result routes in the referred flow (the comparison-group
     * screen). A successful result follows the screen's normal `nextScreen`. */
    rejectedScreen: ScreenNames;
    /** Direct (D2C) flow: where control or clinically-disqualified users route (the 20% outcome). */
    disqualifiedScreen: ScreenNames;
  };

type Props<ScreenNames extends string> = NavigationProps<
  MedicareAccessCoverageLoadingScreenConfig<ScreenNames>,
  ScreenNames
>;

export const MedicareAccessCoverageLoadingScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const userId = useAppStore((state) => state.user.userId);

  useEffect(
    function awaitEligibilityResult() {
      if (userId === undefined) return;

      const currentUserId = userId;
      // Ignore late snapshots after routing finishes or the effect is cancelled during unmount.
      let isInactive = false;
      let completedAttempt: MedicareAccessAttemptLog | undefined;
      let unsubscribeAttempt = (): void => undefined;
      let unsubscribeTrack = (): void => undefined;

      function navigateFromEligibilityResult(controlGroup: boolean): void {
        const state = store.getState();
        const isDirect = isDirectMedicareFlow(state);
        const outcome = getEligibilityOutcome(state, controlGroup);
        const disqualified = outcome !== 'eligible';

        Analytics.track({
          event: Analytics.Event.o_proms_completed,
          period: getMedicareAccessStage(),
          results: disqualified ? 'disqualified' : 'pending_eligibility',
        });

        if (isDirect && !disqualified) {
          Analytics.track({
            event: Analytics.Event.o_insurance_outcome,
            outcome: 'eligible_pending_diagnosis',
          });
        }

        // Control-group users see the comparison-group screen first (which then routes D2C users on
        // to the coverage-results screen). D2C users disqualified on clinical grounds skip to it.
        // Each route resets history so the user cannot navigate back onto this screen, which would
        // only re-await the eligibility result and push them forward again.
        if (outcome === 'controlGroup') {
          props.navigation.goTo(props.rejectedScreen, true);
          return;
        }
        if (outcome === 'disqualified') {
          props.navigation.goTo(props.disqualifiedScreen, true);
          return;
        }
        props.navigation.next(props, { resetHistory: true });
      }

      function finish(controlGroup: boolean): void {
        if (isInactive) return;
        isInactive = true;
        clearTimeout(timeout);
        unsubscribeAttempt();
        unsubscribeTrack();
        navigateFromEligibilityResult(controlGroup);
      }

      function isControlGroup(): boolean {
        return (
          completedAttempt?.result === MedicareAccessEligibilityResultCode.NotEligibleControlGroup
        );
      }

      function handleLifecycleStatus(
        lifecycleStatus: MedicareAccessLifecycleStatus | undefined
      ): void {
        if (
          isInactive ||
          completedAttempt === undefined ||
          lifecycleStatus === undefined ||
          lifecycleStatus === MedicareAccessLifecycleStatus.PendingEligibility
        ) {
          return;
        }
        if (lifecycleStatus === MedicareAccessLifecycleStatus.EligiblePendingDiagnosis) {
          void checkMedicareAccessEligibility().catch(recordError);
        }
        finish(isControlGroup());
      }

      function handleAttempt(attempt: MedicareAccessAttemptLog | undefined): void {
        if (isInactive || attempt === undefined || attempt.status === 'pending') return;
        if (attempt.status === 'failed') {
          finish(false);
          return;
        }
        if (attempt.status !== 'complete') return;
        if (completedAttempt !== undefined) return;

        completedAttempt = attempt;
        const track = MedicareAccessTrack.BehaviouralHealth;
        unsubscribeTrack = medicareAccessUserTrackDocRef({
          userId: currentUserId,
          track,
        }).onSnapshot(
          (snapshot) => handleLifecycleStatus(snapshot.data()?.lifecycleStatus),
          handleListenerError
        );
      }

      function handleListenerError(error: Error): void {
        recordError(error);
        finish(isControlGroup());
      }

      // Upstream failures can leave either document pending indefinitely, so bound the wait.
      const timeout = setTimeout(() => {
        console.warn('Medicare Access eligibility remained pending');
        finish(isControlGroup());
      }, ELIGIBILITY_RESULT_TIMEOUT_MS);

      const track = MedicareAccessTrack.BehaviouralHealth;
      unsubscribeAttempt = medicareAccessAttemptLogsCollectionRef({ userId: currentUserId, track })
        .orderBy('sentAt', 'desc')
        .limit(1)
        .onSnapshot((snapshot) => {
          handleAttempt(snapshot.docs[0]?.data());
        }, handleListenerError);

      return () => {
        isInactive = true;
        clearTimeout(timeout);
        unsubscribeAttempt();
        unsubscribeTrack();
      };
    },
    [userId]
  );

  return (
    <ScreenWrapper>
      <FlowContentBody>
        <AiLoadingLayout {...props.layout} />
      </FlowContentBody>
    </ScreenWrapper>
  );
};
